import { CloseIcon, DownloadIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  AssessmentDocument,
  ConsentForm,
  electronicConsentCheckbox1,
  electronicConsentCheckbox2,
  electronicConsentCheckbox3,
  electronicConsentCheckbox4,
  electronicConsentHeader,
  electronicConsentPreface,
  ErrorWithMessage,
  getDateString,
  WithId,
} from '@hb/shared'
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { downloadFromStorage } from '../../../../../backend'
import { PopUpMessageContext } from '../../../../../contexts/PopUpMessage/PopUpMessageContext'
import { SolidActionButton } from '../../../../Buttons'
import { CollapseError } from '../../../../CollapseError'
import { ContactFormModal } from '../../../../Modals'
import { confirmElectronicConsent } from './constants'
import { ElectronicConsentProvider, useElectronicConsent } from './context'
// import downloadIcon from '../../../../../icons/'
interface ConsentFormCheckboxProps {
  checked: boolean
  setChecked: (checked: boolean) => void
  registerContainerRef: (el: HTMLDivElement) => void
}

const ConsentFormCheckbox = ({
  children,
  checked,
  setChecked,
  registerContainerRef,
}: PropsWithChildren<ConsentFormCheckboxProps>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (containerRef.current) {
      registerContainerRef(containerRef.current)
    }
  }, [registerContainerRef])
  return (
    <Box ref={containerRef} w="100%" px={4} py={2}>
      <Checkbox
        isChecked={checked}
        onChange={e => setChecked(e.target.checked)}
        colorScheme="green"
        spacing={3}
        size="lg"
        sx={{
          WebkitTapHighlightColor: 'transparent',
        }}>
        <Text fontSize="md" position="relative" bottom="2px">
          {children}
        </Text>
      </Checkbox>
    </Box>
  )
}

const electonicConsentContactFormId = 'electronic-consent-contact-form'
const ElectronicConsentFooter = ({
  onConfirm,
  allChecked,
  isLoading,
  error,
}: {
  onConfirm: () => void
  allChecked: boolean
  isLoading: boolean
  error: string | null
}) => {
  const { consent, onClose } = useElectronicConsent()
  const { showMessage } = useContext(PopUpMessageContext)
  const submitText = useMemo(() => {
    if (allChecked)
      return 'By clicking “Submit”, I acknowledge that I have read and agree to the above statements.'
    return 'Please confirm all statements to submit your electronic consent agreement.'
  }, [allChecked])

  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownloadSubmittedAgreement = useCallback(async () => {
    if (!consent || isDownloading) return
    const url = consent.signedStoragePath
    try {
      setIsDownloading(true)
      await downloadFromStorage(url)
      showMessage({
        text: 'Downloaded electronic consent agreement',
        type: 'success',
      })
    } catch (e) {
      console.error(e)
      showMessage({
        text: 'Error downloading electronic consent agreement.',
        type: 'error',
        subText: (e as ErrorWithMessage).message,
      })
    } finally {
      setIsDownloading(false)
    }
  }, [consent, showMessage, isDownloading])

  return (
    <Flex
      bg="gray.100"
      borderTop="1px solid"
      borderColor="gray.200"
      w="100%"
      flexFlow="column"
      borderBottomRadius={6}>
      <CollapseError error={error} />
      <Collapse style={{ width: '100%' }} in={!consent?.signedOn}>
        <Flex py={3} px={4} w="100%" flexFlow={['column', 'column', 'row']} align="center" gap={2}>
          <Flex flex={1} minW="0">
            <Text color="gray.700" fontWeight={500}>
              {submitText}
            </Text>
          </Flex>
          <SolidActionButton
            opacity={allChecked ? 1 : 0.6}
            isLoading={isLoading}
            ml="auto"
            onClick={onConfirm}>
            Submit
          </SolidActionButton>
        </Flex>
      </Collapse>
      <Collapse style={{ width: '100%' }} in={!!consent?.signedOn}>
        <Flex w="100%" flexFlow="column">
          <Flex
            py={3}
            px={4}
            w="100%"
            flexFlow={['column', 'column', 'row']}
            align="center"
            gap={2}>
            <Text flex={1} color="gray.700" fontWeight={500}>
              You signed this agreement on {getDateString(consent?.signedOn ?? 0)}. If you have any
              questions or concerns, please contact us or send an email to info@hamiltonbilling.com.
            </Text>
            <IconButton
              isLoading={isDownloading}
              icon={<DownloadIcon />}
              bg="blackAlpha.200"
              borderRadius="full"
              aria-label="Download"
              onClick={handleDownloadSubmittedAgreement}
            />
          </Flex>
          <Box w="100%" px={2} pb={2}>
            <SolidActionButton w="100%" onClick={() => onClose()}>
              Proceed to Consent Form
            </SolidActionButton>
          </Box>
        </Flex>
      </Collapse>
      <ContactFormModal id={electonicConsentContactFormId} />
    </Flex>
  )
}

const ElectronicConsentHeader = () => {
  const { onClose, consent } = useElectronicConsent()
  return (
    <Box
      borderBottom="1px solid"
      borderColor="gray.200"
      bg="gray.50"
      borderTopRadius={6}
      w="100%"
      px={4}
      py={2}>
      <Flex justify="space-between" align="center">
        <Text fontSize="xl" fontFamily="Hero-New">
          {electronicConsentHeader}
        </Text>
        {!consent?.signedOn ? (
          <IconButton
            borderRadius="full"
            size="xs"
            w={7}
            h={7}
            color="gray.600"
            bg="blackAlpha.200"
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={() => onClose()}
          />
        ) : null}
      </Flex>
      <Text fontFamily="hero-new">{electronicConsentPreface}</Text>
    </Box>
  )
}

type ContainerRefItem = HTMLDivElement | null
type ContainerRefTuple = [ContainerRefItem, ContainerRefItem, ContainerRefItem, ContainerRefItem]
const ElectronicConsentForm = ({
  containerHeight,
  containerWidth,
}: {
  containerHeight: number
  containerWidth: number
}) => {
  const { consent, consentForm, onClose, consentLoading, isOpen } = useElectronicConsent()

  const { width, height } = useMemo(
    () => ({
      width: Math.min(700, containerWidth * 0.95),
      height: Math.min(800, containerHeight * 0.95),
    }),
    [containerHeight, containerWidth],
  )

  const [isConfirming, setIsConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checked, setChecked] = useState<[boolean, boolean, boolean, boolean]>(
    consent?.signedOn ? [true, true, true, true] : [false, false, false, false],
  )

  const allChecked = useMemo(() => checked.every(c => c), [checked])

  const handleConfirm = useCallback(async () => {
    if (isConfirming) return
    if (!allChecked) {
      setError('Please confirm all statements.')
      return
    }
    const toUser = consentForm?.toBeSignedByUser
    if (!toUser) {
      setError('No user listed on this consent form.')
      return
    }
    setIsConfirming(true)
    setError(null)

    try {
      await confirmElectronicConsent({
        patientId: toUser,
      })
      onClose(true)
    } catch (e) {
      console.error(e)
      setError('Error submitting electronic consent agreement.')
    } finally {
      setIsConfirming(false)
    }
  }, [consentForm, isConfirming, allChecked, onClose])

  const checkmarkRefs = useRef<ContainerRefTuple>([null, null, null, null])
  const checkmarksContainerRef = useRef<HTMLDivElement>(null)
  const handleRegisterContainerRef = useCallback(
    (index: number) => (el: HTMLDivElement) => {
      checkmarkRefs.current[index] = el
    },
    [checkmarkRefs],
  )
  const onCheck = useCallback(
    (index: number) => (updated: boolean) => {
      if (consent?.signedOn) return
      const newChecked = [...checked] as [boolean, boolean, boolean, boolean]
      newChecked[index] = updated
      setChecked(newChecked)
      // scroll to first unchecked checkbox
      if (updated) {
        const firstUncheckedIndex = newChecked.findIndex(c => !c)
        if (firstUncheckedIndex === -1) return
        const checkedHeight = checkmarkRefs.current
          ?.slice(0, firstUncheckedIndex)
          .reduce((acc, curr) => acc + (curr?.clientHeight || 0), 0)

        if (!checkedHeight) return
        checkmarksContainerRef.current?.scrollTo({
          top: checkedHeight,
          behavior: 'smooth',
        })
      }
    },
    [consent, checked],
  )

  useEffect(() => {
    if (consent?.signedOn) {
      setChecked([true, true, true, true])
    }
  }, [consent])

  return (
    <Flex
      w={`${width}px`}
      bg="white"
      onClick={e => e.stopPropagation()}
      borderRadius={6}
      maxHeight={`${height}px`}
      flexFlow="column"
      opacity={!isOpen || consentLoading ? 0 : 1}
      transition="opacity 0.3s ease-in-out">
      <ElectronicConsentHeader />
      <VStack
        ref={checkmarksContainerRef}
        spacing={0}
        flex={1}
        overflowY="auto"
        flexFlow="column"
        divider={<Divider />}
        w="100%">
        <ConsentFormCheckbox
          registerContainerRef={handleRegisterContainerRef(0)}
          checked={checked[0]}
          setChecked={onCheck(0)}>
          {electronicConsentCheckbox1}
        </ConsentFormCheckbox>
        <ConsentFormCheckbox
          registerContainerRef={handleRegisterContainerRef(1)}
          checked={checked[1]}
          setChecked={onCheck(1)}>
          {electronicConsentCheckbox2}
        </ConsentFormCheckbox>
        <ConsentFormCheckbox
          registerContainerRef={handleRegisterContainerRef(2)}
          checked={checked[2]}
          setChecked={onCheck(2)}>
          {electronicConsentCheckbox3}
        </ConsentFormCheckbox>
        <ConsentFormCheckbox
          registerContainerRef={handleRegisterContainerRef(3)}
          checked={checked[3]}
          setChecked={onCheck(3)}>
          {electronicConsentCheckbox4}
        </ConsentFormCheckbox>
      </VStack>
      <ElectronicConsentFooter
        onConfirm={handleConfirm}
        isLoading={isConfirming}
        error={error}
        allChecked={allChecked}
      />
    </Flex>
  )
}

export const ElectronicConsentModal = ({
  containerHeight,
  containerWidth,
}: {
  containerHeight: number
  containerWidth: number
}) => {
  const { isOpen, consentForm, consentLoading, consent, onClose } = useElectronicConsent()
  if (!consentForm) return null
  return (
    <Center
      position="absolute"
      opacity={isOpen || consentLoading ? 1 : 0}
      pointerEvents={isOpen || consentLoading ? 'auto' : 'none'}
      onClick={() => {
        if (consent?.signedOn) {
          onClose()
        }
      }}
      transition="opacity 0.3s ease-in-out"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={3}
      bg="rgba(0,0,0,0.5)">
      <ElectronicConsentForm containerHeight={containerHeight} containerWidth={containerWidth} />
    </Center>
  )
}

const ConsentStatusView = () => {
  const { consent, toBeSignedByMe } = useElectronicConsent()
  const text = useMemo(() => {
    if (toBeSignedByMe) {
      return consent
        ? `You submitted the electronic consent agreement on ${getDateString(consent.signedOn)}.`
        : 'You have not yet submitted the  .'
    }
    return consent
      ? `Electronic consent agreement submitted by ${consent?.signedBy}.`
      : 'Electronic consent agreement not yet submitted.'
  }, [consent, toBeSignedByMe])
  return (
    <Flex border="1px solid" borderColor="gray.200" borderRadius={4} p={1} w="100%">
      <Text>{text}</Text>
    </Flex>
  )
}

export const ElectronicConsentViewButton = ({
  variant = 'outline',
}: {
  variant?: 'outline' | 'link'
}) => {
  const { onOpen, consentForm, toBeSignedByMe } = useElectronicConsent()
  if (!consentForm) return null
  if (!toBeSignedByMe) {
    return <ConsentStatusView />
  }
  return (
    <Button bg="whiteAlpha.600" variant={variant} size="sm" onClick={onOpen}>
      Electronic Consent Agreement
    </Button>
  )
}

export const ElectronicConsentWrapper = ({
  document,
  containerHeight,
  containerWidth,
  onCloseDocument,
  children,
}: PropsWithChildren<{
  document: WithId<ConsentForm> | WithId<AssessmentDocument>
  containerHeight: number
  containerWidth: number
  onCloseDocument: () => void
}>) => (
  <ElectronicConsentProvider document={document} onCloseDocument={onCloseDocument}>
    <Box width={`${containerWidth}px`} height={`${containerHeight}px`}>
      {children}
      <ElectronicConsentModal containerHeight={containerHeight} containerWidth={containerWidth} />
    </Box>
  </ElectronicConsentProvider>
)
